import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';

const SchoolRegistration = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);

  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    address: '',
    school_type: '',
    website: '',
    county: '',
    class_system: '',
    class_range: {
      start: '',
      end: ''
    },
    postal_code: '',
    extra_info: {
      motto: '',
      established: ''
    },
    school_admin: {
      email: '',
      phone: '',
      password: ''
    },
    status: 'pending',
    is_active: true
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          from: '/school-registration',
          message: 'Please log in to register a school' 
        }, 
        replace: true 
      });
    }
  }, [isAuthenticated, navigate]);

  const schoolTypeOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'technical', label: 'Technical' },
    { value: 'vocational', label: 'Vocational' }
  ];

  const classSystemOptions = [
    { value: '8-4-4', label: '8-4-4' },
    { value: 'CBC', label: 'CBC' },
    { value: 'international', label: 'International' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = ['name', 'email', 'phone', 'address', 'school_type', 'class_system'];
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Name validation
    if (formData.name && (formData.name.length < 2 || formData.name.length > 255)) {
      newErrors.name = 'Name must be between 2 and 255 characters';
    }

    // Phone validation
    const phoneRegex = /^(\+[1-9]{1,3}[- ]?)?\d{10}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format. Example: +254722000000';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // School type validation
    if (formData.school_type && !schoolTypeOptions.map(opt => opt.value).includes(formData.school_type)) {
      newErrors.school_type = 'Invalid school type';
    }

    // Website validation
    if (formData.website) {
      try {
        new URL(formData.website);
      } catch {
        newErrors.website = 'Invalid website URL. Must include http:// or https://';
      }
    }

    // Class system validation
    if (formData.class_system && !classSystemOptions.map(opt => opt.value).includes(formData.class_system)) {
      newErrors.class_system = 'Invalid class system';
    }

    // Admin validations
    if (!formData.school_admin.email || !emailRegex.test(formData.school_admin.email)) {
      newErrors['school_admin.email'] = 'Valid admin email is required';
    }
    if (!formData.school_admin.phone || !phoneRegex.test(formData.school_admin.phone)) {
      newErrors['school_admin.phone'] = 'Valid admin phone is required';
    }
    if (!formData.school_admin.password || formData.school_admin.password.length < 8) {
      newErrors['school_admin.password'] = 'Password must be at least 8 characters long';
    }

    // Class range validation (if provided)
    if (formData.class_range.start && formData.class_range.end) {
      if (parseInt(formData.class_range.start) > parseInt(formData.class_range.end)) {
        newErrors['class_range'] = 'Start class cannot be greater than end class';
      }
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${parent}.${child}`];
        return newErrors;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        class_range: {
          start: formData.class_range.start,
          end: formData.class_range.end
        },
        extra_info: {
          motto: formData.extra_info.motto,
          established: formData.extra_info.established
        },
        school_admin: {
          email: formData.school_admin.email,
          phone: formData.school_admin.phone,
          password: formData.school_admin.password
        }
      };

      const response = await api.post('/api/v1/admin/schools', submitData);
      const { registration_number, school_id, admin_email, email_sent } = response.data;
      
      setSuccess({
        registration_number,
        admin_email,
        email_sent
      });

      sessionStorage.setItem('schoolRegistrationInfo', JSON.stringify({
        registration_number,
        admin_email,
        email_sent
      }));

      setTimeout(() => {
        navigate(`/`, {
          state: { justRegistered: true }
        });
      }, 3000);

    } catch (err) {
      
      if (err.response?.status === 401) {
        setErrors({ general: 'Your session has expired. Please log in again.' });
        await logout();
        return;
      }
      
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.detail;
        
        if (Array.isArray(validationErrors)) {
          const errorObj = {};
          validationErrors.forEach(error => {
            const fieldPath = error.loc.slice(1).join('.');
            errorObj[fieldPath] = error.msg;
          });
          setErrors(errorObj);
        } else {
          setErrors({ general: err.response.data.detail || 'Validation failed. Please check your input.' });
        }
      } else if (err.response?.status === 400) {
        setErrors({ general: err.response.data.detail });
      } else {
        setErrors({ 
          general: err.response?.data?.detail || 
                  err.message || 
                  'Failed to register school' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow-md rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">School Registration</h2>
        </div>
        
        <div className="p-6">
          {errors.general && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{errors.general}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-6 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-lg font-medium text-green-800 mb-2">Registration Successful!</h3>
              <div className="space-y-2 text-sm text-green-700">
                <p>Registration number: <span className="font-mono font-medium">{success.registration_number}</span></p>
                <p>Admin credentials will be sent to: <span className="font-medium">{success.admin_email}</span></p>
                {success.email_sent ? 
                  <p className="text-green-600">✓ Admin credentials have been sent via email</p> :
                  <p className="text-yellow-600">⚠ Admin credentials will be sent shortly</p>
                }
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">Basic Information</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter school name"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+254722000000"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.address ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="school_type"
                  value={formData.school_type}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.school_type ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select school type</option>
                  {schoolTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.school_type && (
                  <p className="mt-1 text-sm text-red-600">{errors.school_type}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.website ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600">{errors.website}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  County
                </label>
                <input
                  type="text"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.county ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.county && (
                  <p className="mt-1 text-sm text-red-600">{errors.county}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class System <span className="text-red-500">*</span>
                </label>
                <select
                  name="class_system"
                  value={formData.class_system}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.class_system ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Select class system</option>
                  {classSystemOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.class_system && (
                  <p className="mt-1 text-sm text-red-600">{errors.class_system}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Range Start
                  </label>
                  <input
                    type="number"
                    name="class_range.start"
                    value={formData.class_range.start}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors['class_range.start'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors['class_range.start'] && (
                    <p className="mt-1 text-sm text-red-600">{errors['class_range.start']}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Range End
                  </label>
                  <input
                    type="number"
                    name="class_range.end"
                    value={formData.class_range.end}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                      errors['class_range.end'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors['class_range.end'] && (
                    <p className="mt-1 text-sm text-red-600">{errors['class_range.end']}</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors.postal_code ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.postal_code && (
                  <p className="mt-1 text-sm text-red-600">{errors.postal_code}</p>
                )}
              </div>
            </div>

            {/* Extra Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">Extra Information</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Motto
                </label>
                <input
                  type="text"
                  name="extra_info.motto"
                  value={formData.extra_info.motto}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors['extra_info.motto'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors['extra_info.motto'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['extra_info.motto']}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year Established
                </label>
                <input
                  type="text"
                  name="extra_info.established"
                  value={formData.extra_info.established}
                  onChange={handleChange}
                  placeholder="e.g., 1990"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors['extra_info.established'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors['extra_info.established'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['extra_info.established']}</p>
                )}
              </div>
            </div>

            {/* School Administrator Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">School Administrator Details</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="school_admin.email"
                  value={formData.school_admin.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors['school_admin.email'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors['school_admin.email'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['school_admin.email']}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="school_admin.phone"
                  value={formData.school_admin.phone}
                  onChange={handleChange}
                  placeholder="+254722000000"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors['school_admin.phone'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors['school_admin.phone'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['school_admin.phone']}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="school_admin.password"
                  value={formData.school_admin.password}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                    errors['school_admin.password'] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {errors['school_admin.password'] && (
                  <p className="mt-1 text-sm text-red-600">{errors['school_admin.password']}</p>
                )}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
              >
                {loading ? 'Registering...' : 'Register School'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-700">Registering school...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolRegistration;